import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, MapPin, ArrowUpRightIcon } from 'lucide-react';
import { MENU_CATEGORIES } from '../data/menu';
import { WINE_PHOTOS } from '../data/wine';
import type { ViewId } from '../types/navigation';
const LOGO_SRC = 'https://cdn.magicpatterns.com/uploads/ao2DrHXUU5TPkmLZkMQLjL/Namnlos_(Instagram-inlagg_(45)).png';
const INSTAGRAM_URL = 'https://www.instagram.com/lilla.verket/';

// Uppdatera adressen här. Både gatuadress och orten hjälper kartappen hitta rätt.
const ADDRESS_LABEL = 'Lilla Verket';
const ADDRESS_QUERY = 'Lilla Verket, Sverige';

// Uppdatera öppettiderna här.
const OPENING_HOURS = [{
  days: 'Sön–Tors',
  time: '08:00–16:00'
}, {
  days: 'Fre–Lör',
  time: '08:00–22:00'
}];
function buildMapsUrl(): string {
  const query = encodeURIComponent(ADDRESS_QUERY);
  if (typeof navigator === 'undefined') {
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  }
  const ua = navigator.userAgent || '';
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(ua) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isApple ? `https://maps.apple.com/?q=${query}` : `https://www.google.com/maps/search/?api=1&query=${query}`;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
type Props = {
  onNavigate: (view: ViewId) => void;
  onOpenCategory: (categoryId: string) => void;
};
export function Home({
  onNavigate,
  onOpenCategory
}: Props) {
  const [mapsUrl, setMapsUrl] = useState(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS_QUERY)}`);
  useEffect(() => {
    setMapsUrl(buildMapsUrl());
  }, []);
  const cards = MENU_CATEGORIES.map(category => ({
    id: category.id,
    title: category.title,
    tagline: category.tagline,
    image: category.images[0],
    onClick: () => onOpenCategory(category.id)
  }));
  return <>
      {/* Intro */}
      <section className="px-6 pt-16 md:pt-24 pb-20 md:pb-28" data-id="element-340" data-mp="1kmtm6ozo3hvc">
        <motion.div initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} className="max-w-4xl mx-auto text-center flex flex-col items-center" data-id="element-341" data-mp="1ondglksuw7ds">
          <motion.img variants={fadeUp} src={LOGO_SRC} alt="Lilla Verket" className="w-full max-w-[420px] md:max-w-[560px] h-auto" draggable={false} data-id="element-342" data-mp="1fnqjhb75tnqj" />

          <motion.p variants={fadeUp} className="mt-10 font-serif text-sm md:text-base tracking-[0.25em] uppercase text-ink/80" data-id="element-343" data-mp="1tloss26mitz6">
            Bageri <span className="mx-2 text-wine/60" data-id="element-344" data-mp="1yif6iiuyhdhf">•</span>
            Café <span className="mx-2 text-wine/60" data-id="element-345" data-mp="2m91e6911svqw">•</span>
            Vin <span className="mx-2 text-wine/60" data-id="element-346" data-mp="2r0sxbyhvjz70">•</span>
            Ost &amp; Chark
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 w-12 h-px bg-ink/20" data-id="element-347" data-mp="flkapc7z5raj" />

          <motion.h1 variants={fadeUp} className="mt-10 font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-[1.15] text-ink max-w-2xl" data-id="element-348" data-mp="fjhcw60kytv9">
            Ett litet verk i kvarteret — bageri på morgonen, vinbar på kvällen.
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-8 max-w-xl font-sans text-sm md:text-base leading-relaxed text-ink/60" data-id="element-349" data-mp="ken7rubynivh">
            Vi bakar, brygger och häller upp. Mackor och bakverk över disken på
            dagen, ost, chark och riktigt bra vin på glas när det blir kväll.
            Allt görs på plats, i lite för små lokaler, med lite för mycket
            omtanke.
          </motion.p>

          {/* Hours */}
          <motion.div variants={fadeUp} className="mt-12" data-id="element-350" data-mp="1h1rv3v19wsjl">
            <div className="inline-flex flex-col items-center gap-3 px-8 py-5 border border-ink/15 rounded-full" data-id="element-351" data-mp="1c3qd7dfl2z9p">
              <p className="font-sans text-[10px] md:text-xs tracking-[0.25em] uppercase font-medium text-ink/80" data-id="element-352" data-mp="27e50spb3iyda">
                Öppettider
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 font-sans text-[11px] md:text-xs tracking-[0.15em] uppercase text-ink/70" data-id="element-353" data-mp="2cf2eopp2p68z">
                {OPENING_HOURS.map((entry, index) => <React.Fragment key={entry.days} data-id="element-354" data-mp="2qd0qbnc0eoks" data-mpi={index}>
                    <div className="flex items-center gap-2" data-id="element-355" data-mp="2qc7rd8jal3d7">
                      <span className="font-medium text-ink/90" data-id="element-356" data-mp="2vb2a6ah9erxb">
                        {entry.days}
                      </span>
                      <span data-id="element-357" data-mp="3032hinznsn3x">{entry.time}</span>
                    </div>
                    {index < OPENING_HOURS.length - 1 && <span className="hidden md:block text-wine/50" data-id="element-358" data-mp="353hrd5kqfch5">•</span>}
                  </React.Fragment>)}
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center gap-3" data-id="element-359" data-mp="12f8di7fr8edz">
            <button type="button" onClick={() => onNavigate('boka')} className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-ink text-white font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase hover:bg-ink/85 transition-colors duration-200" data-id="element-360" data-mp="yc3dqcn26qt1">
              Boka bord
            </button>
            <button type="button" onClick={() => onNavigate('meny')} className="inline-flex items-center justify-center px-7 py-3.5 rounded-full border border-ink/25 text-ink/80 font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase hover:border-ink hover:text-ink transition-colors duration-200" data-id="element-361" data-mp="1h4xikdqyx2jj">
              Se menyn
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* Lite Vin teaser */}
      <section className="px-6 py-20 md:py-28 border-t border-ink/10" data-id="element-362" data-mp="3uacckry8m14x">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-center" data-id="element-363" data-mp="26jthpjhwotj">
          <div data-id="element-364" data-mp="3klaw70ns5hqf">
            <p className="font-sans text-[10px] md:text-xs tracking-[0.3em] uppercase text-wine" data-id="element-365" data-mp="3kjq21c5do0t3">
              Vårt vinkoncept
            </p>
            <h2 className="mt-5 font-sans text-5xl sm:text-6xl md:text-7xl font-medium tracking-[-0.045em] uppercase leading-[0.85] text-ink" data-id="element-366" data-mp="3phj4pv7h2com">
              Lite Vin
            </h2>
            <p className="mt-6 font-serif text-2xl md:text-3xl font-light text-ink/80" data-id="element-367" data-mp="3fmzmqr1ed98q">
              Bra vin till bra pris.
            </p>
            <p className="mt-6 max-w-md font-sans text-sm md:text-base leading-relaxed text-ink/60" data-id="element-368" data-mp="36kq93sau0zr4">
              Tolv nivåer, allt på glas — helt eller halvt. Vill du testa flera
              tar du en vinbricka.
            </p>
            <button type="button" onClick={() => onNavigate('vin')} className="mt-8 group inline-flex items-center gap-2 font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase text-ink/70 hover:text-ink transition-colors duration-200" data-id="element-369" data-mp="14slkapomqikm">
              <span className="border-b border-ink/30 group-hover:border-ink transition-colors duration-200" data-id="element-370" data-mp="1nqx62tb21u54">
                Läs om Lite Vin
              </span>
              <ArrowUpRightIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-371" data-mp="1xnt7yg6g7i0p" />
            </button>
          </div>
          <img src={WINE_PHOTOS.hero} alt="Gäster som skålar med olika viner vid ett bord" className="w-full aspect-[16/10] object-cover rounded-sm" loading="lazy" data-id="element-372" data-mp="1sphxc38bx3hf" />
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 py-20 md:py-28 border-t border-ink/10" aria-labelledby="kategorier-rubrik" data-id="element-373" data-mp="caqh0oogjmoh">
        <div className="max-w-6xl mx-auto" data-id="element-374" data-mp="89wtvxiuabwn">
          <div className="max-w-xl" data-id="element-375" data-mp="8b7vprsq59l4">
            <p className="font-sans text-[10px] md:text-xs tracking-[0.3em] uppercase text-ink/45" data-id="element-376" data-mp="wubjoprys5l6">
              Menyn
            </p>
            <h2 id="kategorier-rubrik" className="mt-5 font-serif text-4xl md:text-5xl font-light text-ink" data-id="element-377" data-mp="11rv467f99elk">
              Vad är du sugen på?
            </h2>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={{
          visible: {
            transition: {
              staggerChildren: 0.07
            }
          }
        }} className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-378" data-mp="1akomyetwuw46">
            {cards.map(card => <motion.button key={card.id} variants={fadeUp} type="button" onClick={card.onClick} className="group text-left" data-id="element-379" data-mp="rv8lnpaeizmp">
                <div className="relative aspect-[4/3] overflow-hidden bg-ink/5 rounded-sm" data-id="element-380" data-mp="238udji7nbfbx">
                  <div className="w-full h-full transition-transform duration-700 ease-out group-hover:scale-[1.03]" data-id="element-381" data-mp="2dcr6shkj5jvd">
                    <img src={card.image.src} alt="" aria-hidden="true" loading="lazy" className="w-full h-full object-cover" style={{
                  objectPosition: card.image.focus ?? 'center',
                  transform: card.image.zoom ? `scale(${card.image.zoom})` : undefined
                }} draggable={false} data-id="element-382" data-mp="28fragydhfvhg" />
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between gap-4" data-id="element-383" data-mp="1gpw73xssi59s">
                  <div data-id="element-384" data-mp="3f2wcg7ppyx3f">
                    <h3 className="font-sans text-xl md:text-2xl font-medium tracking-[-0.02em] uppercase text-ink" data-id="element-385" data-mp="3f13bn91sf73c">
                      {card.title}
                    </h3>
                    <p className="mt-1 font-sans text-[13px] leading-relaxed text-ink/50" data-id="element-386" data-mp="cms0527min7a">
                      {card.tagline}
                    </p>
                  </div>
                  <ArrowUpRightIcon strokeWidth={1.5} className="mt-1 w-4 h-4 shrink-0 text-ink/30 group-hover:text-ink transition-colors duration-200" data-id="element-387" data-mp="mlptqjjwp6x5" />
                </div>
              </motion.button>)}
          </motion.div>
        </div>
      </section>

      {/* Practical */}
      <section className="px-6 py-20 md:py-24 border-t border-ink/10" data-id="element-388" data-mp="3nwt5nwr992x">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8" data-id="element-389" data-mp="3p7w5lidmcsz">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-390" data-mp="3v0rf1oojymog">
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase text-ink/60 hover:text-ink transition-colors duration-200" aria-label={`Hitta hit till ${ADDRESS_LABEL} — öppnas i din kartapp`} data-id="element-391" data-mp="37bf70s4yoknq">
              <MapPin strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-392" data-mp="18wrbu5xpbley" />
              <span className="border-b border-transparent group-hover:border-ink/40 transition-colors duration-200" data-id="element-393" data-mp="1cw03n596l97v">
                Hitta hit
              </span>
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2.5 text-ink/55 hover:text-ink transition-colors duration-200" aria-label="Följ Lilla Verket på Instagram" data-id="element-394" data-mp="25l71e20t9us2">
              <Instagram strokeWidth={1.5} className="w-4 h-4" data-id="element-395" data-mp="g5zf97cq4ur3" />
              <span className="font-sans text-[11px] md:text-xs tracking-[0.15em] lowercase" data-id="element-396" data-mp="g8vecz0b5ph4">
                @lilla.verket
              </span>
            </a>
          </div>

          <button type="button" onClick={() => onNavigate('forbestall')} className="inline-flex items-center gap-2 font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase text-ink/60 hover:text-ink transition-colors duration-200" data-id="element-397" data-mp="2mtzt8zt4blbg">
            Förbeställ mackor
            <ArrowUpRightIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-398" data-mp="291a6gt5rmcda" />
          </button>
        </div>
      </section>
    </>;
}